import React, { useState, useEffect } from "react";
import {
	getBanners,
	addBanner,
	editBanner,
	deleteBanner,
} from "../../api/apiServiceFetch";
import { DataGrid } from "@mui/x-data-grid";
import {
	Button,
	TextField,
	Box,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Checkbox,
	FormControlLabel,
	MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import "./Banners.scss";

const defaultFormData = {
	id: 0,
	fileName: "",
	name: "",
	description: "",
	sort: 0,

	link1: "",
	linkType1: 0,
	linkButton1: "",

	link2: "",
	linkType2: 0,
	linkButton2: "",

	isActive: false,

	clickCount: 0,
	joinCount: 0,
	maxJoins: "",

	serviceName: "",

	base64String: "",
};

const linkTypeOptions = [
	{ value: 0, label: "Обычная ссылка" },
	{ value: 1, label: "Внутренний переход" },
	{ value: 2, label: "Сервисная ссылка" },
];

const Banners = () => {
	const filePath = process.env.REACT_APP_FILE_PATH;
	const [banners, setBanners] = useState([]);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedBanner, setSelectedBanner] = useState(null);
	const [formData, setFormData] = useState(defaultFormData);
	const [editMode, setEditMode] = useState(false);
	const [error, setError] = useState("");

	const fetchAllBanners = async () => {
		try {
			const bannerData = await getBanners();
			setBanners(Array.isArray(bannerData) ? bannerData : []);
		} catch (error) {
			console.error("Error fetching banners:", error);
		}
	};

	useEffect(() => {
		fetchAllBanners();
	}, []);

	const openDialog = (data = {}, isEdit = false) => {
		setFormData({
			...defaultFormData,
			...data,
			sort: data?.sort ?? 0,
			isActive: data?.isActive ?? false,
			linkType1: data?.linkType1 ?? 0,
			linkType2: data?.linkType2 ?? 0,
			clickCount: data?.clickCount ?? 0,
			joinCount: data?.joinCount ?? 0,
			maxJoins: data?.maxJoins ?? "",
			serviceName: data?.serviceName ?? "",
			base64String: "",
		});

		setEditMode(isEdit);
		setDialogOpen(true);
		setError("");
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setError("");
		setEditMode(false);
		setFormData(defaultFormData);
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]:
				type === "checkbox"
					? checked
					: type === "number"
						? value === ""
							? ""
							: Number(value)
						: value,
		}));
	};

	const saveData = async () => {
		try {
			if (!formData.fileName && !editMode) {
				setError("Изображение обязательно.");
				return;
			}

			const payload = {
				id: formData.id,
				fileName: formData.fileName,
				name: formData.name || null,
				description: formData.description || null,
				sort: Number(formData.sort) || 0,

				link1: formData.link1 || null,
				linkType1: Number(formData.linkType1) || 0,
				linkButton1: formData.linkButton1 || null,

				link2: formData.link2 || null,
				linkType2: Number(formData.linkType2) || 0,
				linkButton2: formData.linkButton2 || null,

				isActive: !!formData.isActive,

				clickCount: Number(formData.clickCount) || 0,
				joinCount: Number(formData.joinCount) || 0,
				maxJoins:
					formData.maxJoins === "" || formData.maxJoins === null
						? null
						: Number(formData.maxJoins),

				serviceName: formData.serviceName || null,

				photoBase64: formData.base64String || null,
			};

			if (editMode) {
				await editBanner(payload);
			} else {
				await addBanner(payload);
			}

			await fetchAllBanners();
			closeDialog();
		} catch (apiError) {
			console.error("Error saving banner:", apiError);
			setError(editMode ? "Ошибка при обновлении." : "Ошибка при добавлении.");
		}
	};

	const deleteRecord = async (id) => {
		try {
			await deleteBanner({ id });
			setBanners((prev) => prev.filter((g) => g.id !== id));
		} catch (error) {
			console.error("Error deleting banner:", error);
		}
	};

	return (
		<Box p={3}>
			<Box mb={4}>
				<Typography variant="h6">Баннеры</Typography>

				<Button variant="contained" onClick={() => openDialog()} sx={{ mb: 1 }}>
					<AddIcon />
				</Button>

				<DataGrid
					rows={banners}
					density="compact"
					hideFooterSelectedRowCount
					columnHeaderHeight={40}
					disableColumnSelector
					rowHeight={70}
					autoHeight
					columns={[
						{
							field: "fileName",
							headerName: "Баннер",
							flex: 1,
							renderCell: (params) => (
								<img
									src={`${filePath}/Banners/${params.row.fileName}`}
									alt="banner"
									style={{
										maxHeight: 50,
										maxWidth: "100%",
										objectFit: "contain",
									}}
								/>
							),
						},
						{ field: "serviceName", headerName: "Сервис", flex: 1 },
						{ field: "name", headerName: "Название", flex: 1.5 },
						{ field: "description", headerName: "Описание", flex: 2 },
						{ field: "link1", headerName: "Ссылка 1", flex: 1.5 },
						{ field: "linkType1", headerName: "Тип ссылки 1", flex: 1 },
						{ field: "linkButton1", headerName: "Текст кнопки 1", flex: 1.2 },
						{ field: "link2", headerName: "Ссылка 2", flex: 1.5 },
						{ field: "linkType2", headerName: "Тип ссылки 2", flex: 1 },
						{ field: "linkButton2", headerName: "Текст кнопки 2", flex: 1.2 },
						{ field: "sort", headerName: "Сорт", flex: 0.8 },
						{ field: "clickCount", headerName: "Клики", flex: 0.8 },
						{ field: "joinCount", headerName: "Вступили", flex: 0.9 },
						{ field: "maxJoins", headerName: "Макс. вступлений", flex: 1 },
						{
							field: "leftToJoin",
							headerName: "Осталось",
							flex: 0.8,
							valueGetter: (_, row) =>
								row.maxJoins == null
									? null
									: row.maxJoins - (row.joinCount || 0),
						},
						{
							field: "canShow",
							headerName: "Можно показать",
							flex: 1,
							valueGetter: (_, row) =>
								row.isActive &&
								(row.maxJoins == null || (row.joinCount || 0) < row.maxJoins)
									? "Да"
									: "Нет",
						},
						{
							field: "isActive",
							headerName: "Активно",
							flex: 0.8,
							renderCell: (params) => (params.row.isActive ? "Да" : "Нет"),
						},
						{
							field: "actions",
							headerName: "Действия",
							flex: 1.2,
							renderCell: (params) => (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										width: "100%",
									}}
								>
									<Button
										color="warning"
										onClick={() => openDialog(params.row, true)}
									>
										<EditIcon />
									</Button>
									<Button
										color="error"
										onClick={() => deleteRecord(params.row.id)}
									>
										<DeleteIcon />
									</Button>
								</Box>
							),
						},
					]}
					pageSizeOptions={[5, 10, 20]}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 5, page: 0 },
						},
					}}
					onRowClick={(params) => {
						setSelectedBanner(params.row.id);
					}}
				/>
			</Box>

			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
				<DialogTitle>
					{editMode ? "Редактировать баннер" : "Добавить баннер"}
				</DialogTitle>

				<DialogContent>
					<TextField
						fullWidth
						margin="normal"
						name="serviceName"
						label="ServiceName"
						value={formData.serviceName || ""}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="name"
						label="Название"
						value={formData.name || ""}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="description"
						label="Описание"
						value={formData.description || ""}
						onChange={handleInputChange}
						multiline
						rows={4}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="link1"
						label="Ссылка 1"
						value={formData.link1 || ""}
						onChange={handleInputChange}
					/>

					<TextField
						select
						fullWidth
						margin="normal"
						name="linkType1"
						label="Тип ссылки 1"
						value={formData.linkType1 ?? 0}
						onChange={handleInputChange}
					>
						{linkTypeOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>

					<TextField
						fullWidth
						margin="normal"
						name="linkButton1"
						label="Текст кнопки 1"
						value={formData.linkButton1 || ""}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="link2"
						label="Ссылка 2"
						value={formData.link2 || ""}
						onChange={handleInputChange}
					/>

					<TextField
						select
						fullWidth
						margin="normal"
						name="linkType2"
						label="Тип ссылки 2"
						value={formData.linkType2 ?? 0}
						onChange={handleInputChange}
					>
						{linkTypeOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>

					<TextField
						fullWidth
						margin="normal"
						name="linkButton2"
						label="Текст кнопки 2"
						value={formData.linkButton2 || ""}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="sort"
						label="Сортировка"
						type="number"
						value={formData.sort}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="clickCount"
						label="ClickCount"
						type="number"
						value={formData.clickCount}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="joinCount"
						label="JoinCount"
						type="number"
						value={formData.joinCount}
						onChange={handleInputChange}
					/>

					<TextField
						fullWidth
						margin="normal"
						name="maxJoins"
						label="MaxJoins"
						type="number"
						value={formData.maxJoins}
						onChange={handleInputChange}
					/>

					<Box mt={2}>
						<input
							type="file"
							accept="image/*"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (!file) return;

								const reader = new FileReader();
								reader.onloadend = () => {
									setFormData((prev) => ({
										...prev,
										fileName: file.name,
										base64String: reader.result.split(",")[1],
									}));
								};
								reader.readAsDataURL(file);
							}}
							id="upload-button-fileName"
							style={{ display: "none" }}
						/>

						<label htmlFor="upload-button-fileName">
							<Button
								variant="contained"
								color="primary"
								component="span"
								sx={{
									margin: "10px 0",
									padding: "10px 20px",
									fontSize: "14px",
								}}
							>
								Загрузить изображение
							</Button>
						</label>

						<Box sx={{ marginTop: "10px", textAlign: "center" }}>
							<Typography variant="subtitle1">Предпросмотр</Typography>

							{(formData.base64String || formData.fileName) && (
								<img
									src={
										formData.base64String
											? `data:image/jpeg;base64,${formData.base64String}`
											: `${filePath}/Banners/${formData.fileName}`
									}
									alt="Preview"
									style={{
										width: "auto",
										height: "100px",
										objectFit: "cover",
										borderRadius: "5px",
										border: "1px solid #ccc",
									}}
								/>
							)}
						</Box>
					</Box>

					<FormControlLabel
						control={
							<Checkbox
								checked={formData.isActive || false}
								onChange={handleInputChange}
								name="isActive"
							/>
						}
						label="Активно"
					/>
				</DialogContent>

				{error && (
					<Typography color="error" sx={{ pl: 2 }}>
						{error}
					</Typography>
				)}

				<DialogActions
					sx={{
						justifyContent: "flex-end",
						padding: "16px",
						backgroundColor: "#f5f5f5",
					}}
				>
					<Button
						onClick={closeDialog}
						sx={{
							backgroundColor: "#f44336",
							color: "#fff",
							fontWeight: "bold",
							padding: "8px 16px",
							borderRadius: "5px",
							"&:hover": {
								backgroundColor: "#d32f2f",
							},
						}}
					>
						Отменить
					</Button>

					<Button
						onClick={saveData}
						sx={{
							backgroundColor: "#4caf50",
							color: "#fff",
							fontWeight: "bold",
							padding: "8px 16px",
							borderRadius: "5px",
							"&:hover": {
								backgroundColor: "#388e3c",
							},
						}}
					>
						{editMode ? "Обновить" : "Сохранить"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default Banners;
